import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useCtaVariant } from "@/lib/cta-variants";
import { useLanguage } from "@/lib/i18n";
import { findArticleBySlug } from "@/blog/articles";

/**
 * Map the current path to its equivalent in the target language.
 * Handles landing, /blog list and individual articles (CA ↔ ES).
 * Returns the original path if no mapping is found.
 */
function pathForLanguage(currentPath, targetLang) {
  // Landing
  if (currentPath === "/") return targetLang === "es" ? "/es" : "/";
  if (currentPath === "/es") return targetLang === "ca" ? "/" : "/es";

  // Blog list
  if (currentPath === "/blog") return targetLang === "es" ? "/es/blog" : "/blog";
  if (currentPath === "/es/blog") return targetLang === "ca" ? "/blog" : "/es/blog";

  // Blog articles — look up the paired slug in the registry
  if (currentPath.startsWith("/blog/")) {
    const slug = currentPath.slice("/blog/".length);
    const found = findArticleBySlug(slug);
    if (found && targetLang === "es") return `/es/blog/${found.article.es.slug}`;
  }
  if (currentPath.startsWith("/es/blog/")) {
    const slug = currentPath.slice("/es/blog/".length);
    const found = findArticleBySlug(slug);
    if (found && targetLang === "ca") return `/blog/${found.article.ca.slug}`;
  }

  // Legal pages — preserve CA/ES variants where available
  const legalMap = {
    "/avis-legal": { es: "/aviso-legal" },
    "/aviso-legal": { ca: "/avis-legal" },
    "/politica-privacitat": { es: "/politica-privacidad" },
    "/politica-privacidad": { ca: "/politica-privacitat" },
  };
  if (legalMap[currentPath] && legalMap[currentPath][targetLang]) {
    return legalMap[currentPath][targetLang];
  }

  return currentPath;
}

function LangBtn({ value, label, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      data-testid={`lang-toggle-${value}`}
      aria-pressed={active}
      className={`px-2 py-1 text-xs font-sans-display tracking-wider rounded transition ${
        active
          ? "text-[#0B2545] font-semibold"
          : "text-slate-400 hover:text-[#475569]"
      }`}
    >
      {label}
    </button>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const cta = useCtaVariant();
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToCTA = (e) => {
    e.preventDefault();
    const el = document.getElementById("final-cta");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  /** Switch UI language AND navigate to the equivalent localized URL. */
  const switchLang = (target) => {
    if (target === lang) return;
    setLang(target);
    const next = pathForLanguage(location.pathname, target);
    if (next !== location.pathname) navigate(next);
  };

  return (
    <header
      className={`sticky top-0 z-50 backdrop-blur-xl transition-all ${
        scrolled
          ? "bg-[#F9F8F6]/85 border-b border-slate-200"
          : "bg-[#F9F8F6]/60 border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a
          href="/"
          data-testid="nav-brand-logo"
          className="flex items-center gap-2 group"
        >
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#B4C5A4] opacity-70 pulse-dot"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#0B2545]"></span>
          </span>
          <span className="font-sans-display font-semibold tracking-tight text-[#0B2545] text-lg">
            AI METABOLIC
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7 text-sm text-[#475569] font-medium">
          <a href="/#problem" className="underline-grow">{t("nav.problem")}</a>
          <Link to="/comparador-glp1" className="underline-grow" data-testid="nav-comparator-link">
            Comparador GLP-1
          </Link>
          <a
            href={lang === "es" ? "/es/blog" : "/blog"}
            className="underline-grow"
            data-testid="nav-blog-link"
          >
            {t("nav.blog")}
          </a>
        </nav>

        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div
            className="hidden sm:flex items-center gap-0 border border-slate-200 rounded-md bg-white/60 backdrop-blur"
            role="group"
            aria-label={t("nav.langSwitch")}
            data-testid="lang-toggle-group"
          >
            <LangBtn value="ca" label="CA" active={lang === "ca"} onSelect={switchLang} />
            <span className="text-slate-300">|</span>
            <LangBtn value="es" label="ES" active={lang === "es"} onSelect={switchLang} />
          </div>

          <button
            onClick={scrollToCTA}
            data-testid="nav-cta-button"
            className="lift-btn hidden sm:inline-flex items-center h-10 px-5 bg-[#0B2545] text-[#F9F8F6] text-sm font-sans-display font-medium tracking-wide rounded-md hover:bg-[#0a1f3a]"
          >
            {cta.navButton}
          </button>
        </div>
      </div>
    </header>
  );
}
